import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/client';
import fetchTaskListAllPages from '../api/fetchTaskListAllPages';
import MarkdownText from '../components/MarkdownText';
import StatementAttachmentLinks from '../components/StatementAttachmentLinks';
import TaskBankSolutionsPeek from '../components/TaskBankSolutionsPeek';

const EGE_CLEANUP_TYPES = buildEgeCleanupTypes();

function buildEgeCleanupTypes() {
  const types = [];
  for (let n = 1; n <= 27; n += 1) {
    if (n === 19) {
      types.push({
        key: '19-21',
        label: '19–21',
        sourceName: 'ЕГЭ 19–21',
      });
      continue;
    }
    if (n === 20 || n === 21) continue;
    types.push({
      key: String(n),
      label: String(n),
      sourceName: `ЕГЭ-${n}`,
    });
    if (n === 7) {
      types.push({
        key: '7-2',
        label: '7-2',
        sourceName: 'ЕГЭ-7-2',
      });
    }
  }
  return types;
}

function typeByKey(key) {
  return EGE_CLEANUP_TYPES.find((t) => t.key === key) || null;
}

function taskSearchHaystack(task) {
  return [
    task.statement,
    task.title,
    task.source_number,
    task.source_name,
    task.source_author,
  ]
    .map((v) => String(v || '').toLowerCase())
    .join('\n');
}

function polyakovLabel(task) {
  const num = task.source_number != null && String(task.source_number).trim() !== ''
    ? String(task.source_number).trim()
    : '—';
  const src = (task.source_name || '').trim();
  return src ? `${src} №${num}` : `№${num}`;
}

function polyakovNumber(task) {
  const raw = String(task?.source_number || '').trim();
  if (!raw) return null;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}

function formatMarkedNumbers(tasks) {
  const nums = tasks.map((t) => {
    const n = String(t.source_number || '').trim();
    return n || String(t.id);
  });
  const shown = nums.slice(0, 24);
  const extra = nums.length > shown.length ? '…' : '';
  return `${shown.join(', ')}${extra}`;
}

function ruPlural(n, one, few, many) {
  const n10 = n % 10;
  const n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return one;
  if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return few;
  return many;
}

/**
 * Админ-инструмент: просмотр банка по типу КИМ и жёсткое удаление лишних задач.
 */
export default function TaskBankCleanupPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const typeKey = (searchParams.get('type') || '').trim();
  const selectedType = typeByKey(typeKey);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [untilNumber, setUntilNumber] = useState('');
  const [markedIds, setMarkedIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const markedCount = markedIds.size;
  const searchNorm = search.trim().toLowerCase();

  const visibleTasks = useMemo(() => {
    if (!searchNorm) return tasks;
    return tasks.filter((task) => taskSearchHaystack(task).includes(searchNorm));
  }, [tasks, searchNorm]);

  const hiddenMarkedCount = useMemo(() => {
    if (!searchNorm || markedCount === 0) return 0;
    const visibleIds = new Set(visibleTasks.map((t) => t.id));
    let n = 0;
    markedIds.forEach((id) => {
      if (!visibleIds.has(id)) n += 1;
    });
    return n;
  }, [searchNorm, markedCount, visibleTasks, markedIds]);

  useEffect(() => {
    setMarkedIds(new Set());
    setSearch('');
    setUntilNumber('');
    setStatusMessage('');
  }, [typeKey]);

  useEffect(() => {
    if (!selectedType) {
      setTasks([]);
      setLoading(false);
      setError('');
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    setStatusMessage('');

    const qs = new URLSearchParams();
    qs.set('source_name', selectedType.sourceName);
    qs.append('tag', 'ege');
    qs.set('catalog_sort', 'source');
    qs.set('catalog_order', 'asc');
    qs.set('include_statement', '1');
    qs.set('page_size', '200');

    fetchTaskListAllPages(`/tasks/?${qs.toString()}`)
      .then((list) => {
        if (cancelled) return;
        setTasks(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Не удалось загрузить задачи этого типа.');
        setTasks([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedType]);

  const setTypeInUrl = useCallback(
    (key) => {
      const next = new URLSearchParams();
      if (key) next.set('type', key);
      setSearchParams(next, { replace: true });
    },
    [setSearchParams],
  );

  function selectType(nextType) {
    if (!nextType) return;
    if (selectedType?.key === nextType.key) return;
    if (markedCount > 0) {
      const ok = window.confirm(
        `Сбросить ${markedCount} ${ruPlural(markedCount, 'пометку', 'пометки', 'пометок')} и перейти к типу ${nextType.label}?`,
      );
      if (!ok) return;
    }
    setTypeInUrl(nextType.key);
  }

  function toggleMarked(taskId) {
    setMarkedIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  }

  function markUntilPolyakov(maxNum) {
    if (!Number.isFinite(maxNum)) return 0;
    const ids = tasks
      .filter((task) => {
        const n = polyakovNumber(task);
        return n != null && n <= maxNum;
      })
      .map((task) => task.id);
    setMarkedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
    setStatusMessage(
      ids.length
        ? `Помечено с начала до №${maxNum}: ${ids.length} ${ruPlural(ids.length, 'задача', 'задачи', 'задач')}.`
        : `Нет задач с номером Полякова ≤ ${maxNum}.`,
    );
    return ids.length;
  }

  function markUntilTypedNumber() {
    const maxNum = Number.parseInt(String(untilNumber).trim(), 10);
    if (!Number.isFinite(maxNum) || maxNum < 0) {
      setStatusMessage('Укажите номер Полякова, до которого пометить задачи.');
      return;
    }
    markUntilPolyakov(maxNum);
  }

  async function deleteMarked() {
    if (!selectedType || markedCount === 0 || deleting) return;
    const markedTasks = tasks.filter((t) => markedIds.has(t.id));
    const ids = markedTasks.map((t) => t.id);
    const nums = formatMarkedNumbers(markedTasks);
    const ok = window.confirm(
      `Удалить ${ids.length} ${ruPlural(ids.length, 'задачу', 'задачи', 'задач')} типа ${selectedType.label} навсегда?\n№ ${nums}\nСвязанные попытки и пункты списков тоже удалятся.`,
    );
    if (!ok) return;

    setDeleting(true);
    setError('');
    setStatusMessage('');
    try {
      const res = await api.post('/tasks/editor/bulk-delete/', { task_ids: ids });
      const deleted = res.data?.deleted ?? ids.length;
      const gone = new Set(ids);
      setTasks((prev) => prev.filter((t) => !gone.has(t.id)));
      setMarkedIds(new Set());
      setStatusMessage(`Удалено задач: ${deleted}.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Не удалось удалить помеченные задачи.');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="task-bank-cleanup-page">
      <nav className="task-cleanup-types" role="tablist" aria-label="Типы КИМ">
        {EGE_CLEANUP_TYPES.map((type) => (
          <button
            key={type.key}
            type="button"
            role="tab"
            aria-selected={selectedType?.key === type.key}
            className={`task-cleanup-type-btn${selectedType?.key === type.key ? ' is-active' : ''}`}
            onClick={() => selectType(type)}
          >
            {type.label}
          </button>
        ))}
      </nav>

      <div className="task-cleanup-main">
        <header className="task-cleanup-toolbar">
          <div>
            <h1 className="task-cleanup-title">Очистка банка</h1>
            <p className="task-cleanup-lead text-secondary">
              Выберите тип, пометьте лишние корзиной, затем нажмите «Удалить».
            </p>
          </div>
          <button
            type="button"
            className="btn task-cleanup-delete-btn"
            disabled={!selectedType || markedCount === 0 || deleting}
            onClick={deleteMarked}
          >
            <i className="fa-solid fa-trash" aria-hidden />
            {deleting
              ? 'Удаление…'
              : markedCount > 0
                ? `Удалить (${markedCount})`
                : 'Удалить'}
          </button>
        </header>

        {error && <div className="alert alert-error">{error}</div>}
        {statusMessage && <div className="alert alert-success">{statusMessage}</div>}

        <div className="task-cleanup-search">
          <input
            type="search"
            className="form-input"
            placeholder="Поиск по тексту задачи или номеру Полякова"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={!selectedType}
            aria-label="Поиск по тексту задачи"
          />
          <form
            className="task-cleanup-until"
            onSubmit={(e) => {
              e.preventDefault();
              markUntilTypedNumber();
            }}
          >
            <label className="task-cleanup-until-label" htmlFor="task-cleanup-until-number">
              До №
            </label>
            <input
              id="task-cleanup-until-number"
              type="number"
              min="0"
              step="1"
              inputMode="numeric"
              className="form-input task-cleanup-until-input"
              placeholder="120"
              value={untilNumber}
              onChange={(e) => setUntilNumber(e.target.value)}
              disabled={!selectedType || loading || tasks.length === 0}
              aria-label="Пометить все задачи с начала до этого номера Полякова"
            />
            <button
              type="submit"
              className="btn btn-outline task-cleanup-until-btn"
              disabled={!selectedType || loading || tasks.length === 0}
            >
              Пометить до №
            </button>
          </form>
        </div>

        {selectedType ? (
          <p className="task-cleanup-meta text-secondary">
            {loading
              ? `Загрузка типа ${selectedType.label}…`
              : `Тип ${selectedType.label}: показано ${visibleTasks.length} из ${tasks.length} · помечено ${markedCount}`}
            {hiddenMarkedCount > 0
              ? ` (из них ${hiddenMarkedCount} ${ruPlural(hiddenMarkedCount, 'скрыта', 'скрыты', 'скрыты')} поиском — всё равно удалятся)`
              : ''}
          </p>
        ) : (
          <p className="task-cleanup-meta text-secondary">Выберите номер типа, чтобы загрузить задачи.</p>
        )}

        {!selectedType ? null : loading ? (
          <p className="text-secondary">Загрузка задач…</p>
        ) : tasks.length === 0 ? (
          <p className="text-secondary">Нет опубликованных задач этого типа.</p>
        ) : visibleTasks.length === 0 ? (
          <p className="text-secondary">Ничего не найдено по текущему поиску.</p>
        ) : (
          <ul className="task-cleanup-list">
            {visibleTasks.map((task) => {
              const marked = markedIds.has(task.id);
              return (
                <li
                  key={task.id}
                  className={`task-cleanup-card${marked ? ' is-marked' : ''}`}
                >
                  <div className="task-cleanup-card-body">
                    <div className="task-cleanup-card-head">
                      <span className="task-cleanup-polyakov">{polyakovLabel(task)}</span>
                    </div>
                    <StatementAttachmentLinks
                      attachments={task.statement_attachments}
                      variant="catalog"
                    />
                    {task.statement ? (
                      <MarkdownText className="statement-text" text={task.statement} />
                    ) : (
                      <p className="text-secondary">Нет текста условия.</p>
                    )}
                    <TaskBankSolutionsPeek task={task} />
                  </div>
                  <div className="task-cleanup-card-actions">
                    <button
                      type="button"
                      className="btn btn-outline task-cleanup-until-this-btn"
                      onClick={() => {
                        const n = polyakovNumber(task);
                        if (n == null) {
                          setStatusMessage('У этой задачи нет номера Полякова.');
                          return;
                        }
                        setUntilNumber(String(n));
                        markUntilPolyakov(n);
                      }}
                    >
                      Все до этой
                    </button>
                    <button
                      type="button"
                      className={`btn task-cleanup-mark-btn${marked ? ' is-on' : ''}`}
                      onClick={() => toggleMarked(task.id)}
                      aria-pressed={marked}
                    >
                      <i className="fa-solid fa-trash" aria-hidden />
                      {marked ? 'Снять пометку' : 'Пометить на удаление'}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
